import { useId, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TaskSplitError } from '../errors';
import { useSplitTask } from '../hooks/useSplitTask';

const MAX_TITLE = 200;

/**
 * Names the new task and confirms moving the ticked subtasks into it
 * (spec 137 US3). Validation runs on submit, so a half-typed name is not
 * scolded; the service enforces the same 1–200 bound.
 *
 * Closing is blocked while the request is in flight. A refusal stays inline,
 * with the service's own wording, so the person can adjust and retry.
 */
export function SplitTaskDialog({
  workspaceId,
  parent,
  childTaskIds,
  open,
  onOpenChange,
  onSplit,
}: {
  workspaceId: string;
  parent: { id: string; title: string };
  childTaskIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSplit: () => void;
}) {
  const split = useSplitTask();
  const { error, isPending } = split;
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  // A double submit can land before React re-renders with `isPending`.
  const sending = useRef(false);
  const inputId = useId();
  const errorId = useId();

  const count = childTaskIds.length;
  const noun = count === 1 ? 'subtask' : 'subtasks';
  // Not only the typed refusals: a network failure or anything unexpected must
  // not leave the dialog quiet.
  const failure =
    error === null
      ? null
      : error instanceof TaskSplitError
        ? error.message
        : 'The subtasks could not be moved. Check your connection and try again.';

  const reset = () => {
    setName('');
    setNameError(null);
    split.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    onOpenChange(next);
    if (!next) reset();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (isPending || sending.current) return;
    const title = name.trim();
    if (title.length === 0) {
      setNameError('Give the new task a name');
      return;
    }
    if (title.length > MAX_TITLE) {
      setNameError(`Keep the name to ${MAX_TITLE} characters or fewer`);
      return;
    }
    setNameError(null);
    sending.current = true;
    split.mutate(
      { workspaceId, parentId: parent.id, title, childTaskIds },
      {
        onSettled: () => {
          sending.current = false;
        },
        onSuccess: (response) => {
          toast(`Moved ${count} ${noun} to ${response.task.title}`);
          reset();
          onOpenChange(false);
          onSplit();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <DialogHeader>
            <DialogTitle>Move to new task</DialogTitle>
            <DialogDescription>
              The new task joins {parent.title}&apos;s step and waits on the
              subtasks you picked.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={inputId}>New task name</Label>
            <Input
              id={inputId}
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={isPending}
              autoComplete="off"
              aria-invalid={nameError !== null}
              aria-describedby={nameError !== null ? errorId : undefined}
            />
            {nameError !== null && (
              <p id={errorId} className="text-destructive text-sm">
                {nameError}
              </p>
            )}
          </div>

          {failure !== null && (
            <p role="alert" className="text-destructive text-sm">
              {failure}
            </p>
          )}

          <DialogFooter>
            <DialogClose
              render={
                <Button type="button" variant="outline" disabled={isPending} />
              }
            >
              Cancel
            </DialogClose>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Moving…' : `Move ${count} ${noun}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
